interface CodeItem {
  code: string
}

export interface PerimeterSelectionInput {
  client?: CodeItem | null
  portfolios?: CodeItem[]
  valuationDate?: string | null
  compartments?: CodeItem[]
  lookThroughLevel?: number | null
}

function hasCodeItems(items?: CodeItem[]): boolean {
  return Array.isArray(items) && items.length > 0 && items.every(item => !!item?.code)
}

export function getPerimeterValidationErrors(input: PerimeterSelectionInput): string[] {
  const errors: string[] = []

  if (!input.client?.code) {
    errors.push('client manquant')
  }

  if (!hasCodeItems(input.portfolios)) {
    errors.push('au moins un portefeuille doit etre selectionne')
  }

  if (!input.valuationDate) {
    errors.push('date de valorisation manquante')
  }

  if (!hasCodeItems(input.compartments)) {
    errors.push('au moins un compartiment doit etre selectionne')
  }

  if (input.lookThroughLevel === undefined || input.lookThroughLevel === null) {
    errors.push('niveau de transparisation manquant')
  }

  return errors
}

export function assertPerimeterSelection(
  input: PerimeterSelectionInput,
  context = 'Perimetre invalide'
): void {
  const errors = getPerimeterValidationErrors(input)

  if (errors.length > 0) {
    throw new Error(`${context}: ${errors.join(', ')}`)
  }
}

export function isPerimeterSelectionComplete(input: PerimeterSelectionInput): boolean {
  return getPerimeterValidationErrors(input).length === 0
}
interface CompartmentItem extends CodeItem {
  portfolioCode?: string
}

/**
 * Deux compartiments sont identiques s'ils ont le même code (sans casse) et, quand les deux
 * le connaissent, le même portefeuille : un même code peut exister dans deux portefeuilles.
 */
export const isSameCompartment = (a: CompartmentItem, s: CompartmentItem): boolean =>
  a.code.trim().toLowerCase() === s.code.trim().toLowerCase() &&
  (!s.portfolioCode || !a.portfolioCode || a.portfolioCode === s.portfolioCode)

/**
 * Ne garde de la sélection que les compartiments présents dans la liste disponible
 * (chargée pour les portefeuilles et la date courants), en renvoyant les objets de la liste
 * disponible : une sélection restaurée de session, qui peut ne porter ni son code portefeuille
 * ni la casse d'origine du code, est ainsi complétée. La correspondance porte sur le code (sans
 * casse) et, quand les deux côtés le connaissent, sur le portefeuille.
 */
export function retainAvailableCompartments<T extends CompartmentItem>(
  selected: CompartmentItem[],
  available: T[]
): T[] {
  const retained: T[] = []
  for (const s of selected) {
    for (const a of available) {
      if (isSameCompartment(a, s) && !retained.includes(a)) retained.push(a)
    }
  }
  return retained
}

/**
 * Nombre de compartiments de la sélection qui n'ont aucune correspondance dans la liste disponible.
 */
export function countUnavailableCompartments(
  selected: CompartmentItem[],
  available: CompartmentItem[]
): number {
  return selected.filter(s => !available.some(a => isSameCompartment(a, s))).length
}

/**
 * Retire de la sélection les compartiments rattachés à un portefeuille qui n'est plus
 * sélectionné. Un compartiment dont le portefeuille est inconnu est conservé : il sera
 * tranché par retainAvailableCompartments au chargement de la liste.
 */
export function retainCompartmentsOfPortfolios<T extends CompartmentItem>(
  selected: T[],
  portfolioCodes: string[]
): T[] {
  return selected.filter(s => !s.portfolioCode || portfolioCodes.includes(s.portfolioCode))
}
